import { useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import {
  HiAcademicCap,
  HiArrowRight,
  HiBriefcase,
  HiCheckBadge,
} from "react-icons/hi2";

/* =========================================================
   CATEGORY VISUAL SYSTEM
   ========================================================= */

const categoryStyles = {
  "Web Development": {
    accent: "from-indigo-500 via-blue-500 to-cyan-400",
    icon:
      "border-indigo-200/80 bg-indigo-50 text-indigo-600 dark:border-indigo-900/60 dark:bg-indigo-950/50 dark:text-indigo-300",
    dot: "bg-indigo-500",
    text: "text-indigo-600 dark:text-indigo-400",
  },

  Programming: {
    accent: "from-violet-500 via-purple-500 to-indigo-500",
    icon:
      "border-violet-200/80 bg-violet-50 text-violet-600 dark:border-violet-900/60 dark:bg-violet-950/50 dark:text-violet-300",
    dot: "bg-violet-500",
    text: "text-violet-600 dark:text-violet-400",
  },

  "Data Analytics": {
    accent: "from-cyan-500 via-sky-500 to-blue-500",
    icon:
      "border-cyan-200/80 bg-cyan-50 text-cyan-600 dark:border-cyan-900/60 dark:bg-cyan-950/50 dark:text-cyan-300",
    dot: "bg-cyan-500",
    text: "text-cyan-600 dark:text-cyan-400",
  },

  "Data Science": {
    accent: "from-purple-500 via-fuchsia-500 to-pink-500",
    icon:
      "border-purple-200/80 bg-purple-50 text-purple-600 dark:border-purple-900/60 dark:bg-purple-950/50 dark:text-purple-300",
    dot: "bg-purple-500",
    text: "text-purple-600 dark:text-purple-400",
  },

  "AI/ML": {
    accent: "from-fuchsia-500 via-purple-500 to-indigo-500",
    icon:
      "border-fuchsia-200/80 bg-fuchsia-50 text-fuchsia-600 dark:border-fuchsia-900/60 dark:bg-fuchsia-950/50 dark:text-fuchsia-300",
    dot: "bg-fuchsia-500",
    text: "text-fuchsia-600 dark:text-fuchsia-400",
  },

  Marketing: {
    accent: "from-rose-500 via-pink-500 to-fuchsia-500",
    icon:
      "border-rose-200/80 bg-rose-50 text-rose-600 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-300",
    dot: "bg-rose-500",
    text: "text-rose-600 dark:text-rose-400",
  },

  "IT Infrastructure": {
    accent: "from-emerald-500 via-teal-500 to-cyan-500",
    icon:
      "border-emerald-200/80 bg-emerald-50 text-emerald-600 dark:border-emerald-900/60 dark:bg-emerald-950/50 dark:text-emerald-300",
    dot: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
  },

  "QA/Automation": {
    accent: "from-orange-500 via-amber-500 to-yellow-400",
    icon:
      "border-orange-200/80 bg-orange-50 text-orange-600 dark:border-orange-900/60 dark:bg-orange-950/50 dark:text-orange-300",
    dot: "bg-orange-500",
    text: "text-orange-600 dark:text-orange-400",
  },

  Electronics: {
    accent: "from-sky-500 via-cyan-500 to-teal-400",
    icon:
      "border-sky-200/80 bg-sky-50 text-sky-600 dark:border-sky-900/60 dark:bg-sky-950/50 dark:text-sky-300",
    dot: "bg-sky-500",
    text: "text-sky-600 dark:text-sky-400",
  },

  Design: {
    accent: "from-pink-500 via-rose-500 to-orange-400",
    icon:
      "border-pink-200/80 bg-pink-50 text-pink-600 dark:border-pink-900/60 dark:bg-pink-950/50 dark:text-pink-300",
    dot: "bg-pink-500",
    text: "text-pink-600 dark:text-pink-400",
  },

  ERP: {
    accent: "from-slate-500 via-gray-600 to-zinc-700",
    icon:
      "border-slate-200/80 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300",
    dot: "bg-slate-500",
    text: "text-slate-600 dark:text-slate-300",
  },

  "Professional Skills": {
    accent: "from-blue-500 via-indigo-500 to-violet-500",
    icon:
      "border-blue-200/80 bg-blue-50 text-blue-600 dark:border-blue-900/60 dark:bg-blue-950/50 dark:text-blue-300",
    dot: "bg-blue-500",
    text: "text-blue-600 dark:text-blue-400",
  },
};

const defaultStyle = {
  accent: "from-indigo-500 via-blue-500 to-cyan-400",
  icon:
    "border-indigo-200/80 bg-indigo-50 text-indigo-600 dark:border-indigo-900/60 dark:bg-indigo-950/50 dark:text-indigo-300",
  dot: "bg-indigo-500",
  text: "text-indigo-600 dark:text-indigo-400",
};

const labelStyles = {
  Featured:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300",

  Popular:
    "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900/60 dark:bg-indigo-950/40 dark:text-indigo-300",

  New:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300",
};

/* =========================================================
   COURSE CARD
   ========================================================= */

const CourseCard = ({
  title = "Course",
  slug = "",
  icon,
  description = "",
  category = "Professional Training",
  level = "Beginner",
  certificate = true,
  placements = true,
  rating = null,
  students = null,
  label = "",
}) => {
  const cardRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  /* ---------------------------------------------------------
     Motion values for subtle premium 3D interaction
     --------------------------------------------------------- */

  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const spotlightX = useMotionValue(50);
  const spotlightY = useMotionValue(35);

  const springRotateX = useSpring(rotateX, {
    stiffness: 240,
    damping: 24,
    mass: 0.6,
  });

  const springRotateY = useSpring(rotateY, {
    stiffness: 240,
    damping: 24,
    mass: 0.6,
  });

  const handlePointerMove = (event) => {
    if (prefersReducedMotion || !cardRef.current) {
      return;
    }

    const rect = cardRef.current.getBoundingClientRect();

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const percentX = (x / rect.width) * 100;
    const percentY = (y / rect.height) * 100;

    spotlightX.set(percentX);
    spotlightY.set(percentY);

    const rotateAmountX = ((percentY - 50) / 50) * -2.2;
    const rotateAmountY = ((percentX - 50) / 50) * 2.2;

    rotateX.set(rotateAmountX);
    rotateY.set(rotateAmountY);
  };

  const handlePointerLeave = () => {
    if (prefersReducedMotion) {
      return;
    }

    rotateX.set(0);
    rotateY.set(0);

    spotlightX.set(50);
    spotlightY.set(35);
  };

  const coursePath = slug ? `/courses/${slug}` : "/courses";
  const style = categoryStyles[category] || defaultStyle;

  return (
    <motion.article
      ref={cardRef}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      style={{
        rotateX: springRotateX,
        rotateY: springRotateY,
        transformPerspective: 1200,
      }}
      whileHover={
        prefersReducedMotion
          ? undefined
          : {
              y: -4,
            }
      }
      className="
        group
        relative
        flex
        h-full
        min-h-[330px]
        flex-col
        overflow-hidden
        rounded-[22px]
        border
        border-gray-200/80
        bg-white
        shadow-[0_6px_26px_rgba(15,23,42,0.055)]
        transition-[border-color,box-shadow]
        duration-300
        hover:border-gray-300
        hover:shadow-[0_18px_45px_rgba(15,23,42,0.11)]
        dark:border-gray-800
        dark:bg-gray-900
        dark:hover:border-gray-700
        dark:hover:shadow-[0_18px_45px_rgba(0,0,0,0.28)]
      "
    >
      {/* =====================================================
          Dynamic cursor spotlight
         ===================================================== */}

      <motion.div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          inset-0
          z-0
          opacity-0
          transition-opacity
          duration-300
          group-hover:opacity-100
        "
        style={{
          background: `radial-gradient(
            180px circle at ${spotlightX.get()}% ${spotlightY.get()}%,
            rgba(99,102,241,0.10),
            transparent 65%
          )`,
        }}
      />

      {/* =====================================================
          Premium accent line
         ===================================================== */}

      <div
        aria-hidden="true"
        className={`
          absolute
          inset-x-0
          top-0
          z-10
          h-[3px]
          bg-gradient-to-r
          ${style.accent}
        `}
      />

      <div className="relative z-10 flex h-full flex-col px-4 pb-4 pt-5 sm:px-5 sm:pb-5">

        {/* ===================================================
            TOP HEADER
           =================================================== */}

        <div className="flex items-start justify-between gap-3">

          {/* Icon */}
          <div
            className={`
              relative
              flex
              h-[50px]
              w-[50px]
              shrink-0
              items-center
              justify-center
              rounded-[16px]
              border
              bg-gradient-to-br
              shadow-sm
              transition-transform
              duration-300
              group-hover:scale-[1.04]
              ${style.icon}
            `}
          >
            {/* Icon glow */}
            <div
              aria-hidden="true"
              className="
                absolute
                inset-0
                rounded-[16px]
                opacity-0
                blur-md
                transition-opacity
                duration-300
                group-hover:opacity-60
              "
            />

            <span
              aria-hidden="true"
              className="relative text-[23px]"
            >
              {icon}
            </span>
          </div>

          {/* Status */}
          {label && (
            <span
              className={`
                inline-flex
                shrink-0
                items-center
                rounded-full
                border
                px-2.5
                py-1
                text-[9px]
                font-bold
                uppercase
                tracking-[0.08em]
                ${labelStyles[label] || labelStyles.Popular}
              `}
            >
              <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current opacity-70" />
              {label}
            </span>
          )}
        </div>

        {/* ===================================================
            CATEGORY
           =================================================== */}

        <div className="mt-4 flex items-center gap-2">
          <span
            aria-hidden="true"
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`}
          />

          <p
            className={`
              truncate
              text-[9px]
              font-bold
              uppercase
              tracking-[0.15em]
              ${style.text}
            `}
          >
            {category}
          </p>
        </div>

        {/* ===================================================
            COURSE TITLE
           =================================================== */}

        <h3
          className="
            mt-1.5
            min-h-[51px]
            text-[18px]
            font-bold
            leading-[1.4]
            tracking-[-0.02em]
            text-gray-950
            dark:text-white
          "
        >
          <Link
            to={coursePath}
            className="
              rounded
              outline-none
              transition-colors
              hover:text-indigo-600
              focus-visible:ring-2
              focus-visible:ring-indigo-500
              focus-visible:ring-offset-2
              dark:hover:text-indigo-400
              dark:focus-visible:ring-offset-gray-900
            "
          >
            {title}
          </Link>
        </h3>

        {/* ===================================================
            DESCRIPTION
           =================================================== */}

        <p
          className="
            mt-1
            min-h-[60px]
            line-clamp-3
            text-[12.5px]
            leading-[1.62]
            text-gray-600
            dark:text-gray-300
          "
        >
          {description}
        </p>

        {/* ===================================================
            COURSE METADATA
           =================================================== */}

        <div
          className="
            mt-4
            flex
            min-h-[34px]
            items-center
            gap-x-3
            border-y
            border-gray-100
            py-2.5
            text-[10.5px]
            font-medium
            text-gray-600
            dark:border-gray-800
            dark:text-gray-300
          "
        >
          {/* Level */}
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <HiAcademicCap
              aria-hidden="true"
              className={`shrink-0 text-sm ${style.text}`}
            />

            <span className="truncate">
              {level}
            </span>
          </span>

          {/* Rating */}
          {rating !== null && (
            <>
              <span
                aria-hidden="true"
                className="h-3 w-px shrink-0 bg-gray-200 dark:bg-gray-700"
              />

              <span
                className="inline-flex shrink-0 items-center gap-1"
                aria-label={`Rated ${rating.toFixed(1)} out of 5`}
              >
                <span
                  aria-hidden="true"
                  className="text-amber-500"
                >
                  ★
                </span>

                <span>{rating.toFixed(1)}</span>
              </span>
            </>
          )}

          {/* Learners */}
          {students !== null && (
            <>
              <span
                aria-hidden="true"
                className="h-3 w-px shrink-0 bg-gray-200 dark:bg-gray-700"
              />

              <span className="shrink-0">
                {students.toLocaleString()}+
              </span>
            </>
          )}
        </div>

        {/* ===================================================
            TRUST SIGNALS
           =================================================== */}

        {(certificate || placements) && (
          <div className="mt-3 flex min-h-[20px] flex-wrap items-center gap-x-4 gap-y-1.5">
            {certificate && (
              <span
                className="
                  inline-flex
                  items-center
                  gap-1
                  text-[10px]
                  font-semibold
                  text-gray-600
                  dark:text-gray-300
                "
              >
                <HiCheckBadge
                  aria-hidden="true"
                  className="text-sm text-emerald-500"
                />
                Certificate
              </span>
            )}

            {placements && (
              <span
                className="
                  inline-flex
                  items-center
                  gap-1
                  text-[10px]
                  font-semibold
                  text-gray-600
                  dark:text-gray-300
                "
              >
                <HiBriefcase
                  aria-hidden="true"
                  className="text-sm text-blue-500"
                />
                Placement Support
              </span>
            )}
          </div>
        )}

        {/* ===================================================
            CTA
           =================================================== */}

        <div className="mt-auto pt-4">
          <Link
            to={coursePath}
            aria-label={`View ${title} course details`}
            className="
              group/cta
              flex
              min-h-10
              w-full
              items-center
              justify-between
              rounded-xl
              border
              border-gray-200
              bg-gray-50/90
              px-3.5
              py-2.5
              text-[12px]
              font-bold
              text-gray-800
              shadow-sm
              backdrop-blur-sm
              transition-all
              duration-300
              hover:border-indigo-200
              hover:bg-indigo-600
              hover:text-white
              hover:shadow-md
              focus:outline-none
              focus-visible:ring-2
              focus-visible:ring-indigo-500
              focus-visible:ring-offset-2
              dark:border-gray-700
              dark:bg-gray-800/80
              dark:text-gray-100
              dark:hover:border-indigo-500
              dark:hover:bg-indigo-500
              dark:hover:text-white
              dark:focus-visible:ring-offset-gray-900
            "
          >
            <span>
              View Course Details
            </span>

            <span
              aria-hidden="true"
              className="
                flex
                h-6
                w-6
                shrink-0
                items-center
                justify-center
                rounded-full
                bg-white
                text-gray-500
                shadow-sm
                transition-all
                duration-300
                group-hover/cta:translate-x-0.5
                group-hover/cta:bg-white/20
                group-hover/cta:text-white
                dark:bg-gray-900
                dark:text-gray-300
                dark:group-hover/cta:bg-white/20
                dark:group-hover/cta:text-white
              "
            >
              <HiArrowRight className="text-[11px]" />
            </span>
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default CourseCard;